import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Modal from '../Common/Modal'; // Correction: Import par défaut
import Input from '../Forms/Input'; // Correction: Import par défaut
import Select from '../Forms/Select';
import { useInterviewTemplates } from '../../hooks/useInterviewTemplates'; // Importer le nouveau hook
import { NewInterviewData } from '../../types'; // Utiliser InterviewTemplate

interface NewInterviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<NewInterviewData>) => void; // Rendre les données partielles car tout n'est pas requis pour Cadrage
  templateType?: 'Audit' | 'Cadrage'; // Ajouter la prop pour distinguer le contexte
}

export const NewInterviewModal: React.FC<NewInterviewModalProps> = ({
  isOpen,
  onClose,
  onSubmit: handleFormSubmit,
  templateType = 'Cadrage', // Valeur par défaut alignée avec le type unifié
}) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Partial<NewInterviewData>>(); // Utiliser Partial car tous les champs ne sont pas toujours requis

  // Utiliser le hook pour récupérer les templates filtrés par type
  const {
    templates: availableTemplates,
    isLoading: isLoadingTemplates,
    error: errorTemplates
  } = useInterviewTemplates(templateType?.toLowerCase() as 'audit' | 'cadrage' | undefined);

  const processSubmit: SubmitHandler<Partial<NewInterviewData>> = (data) => {
    handleFormSubmit(data);
    reset(); // Réinitialiser le formulaire après soumission
    // onClose(); // La fermeture est gérée par le composant parent après la soumission logique
  };

  // Gérer la fermeture et la réinitialisation
  const handleClose = () => {
    reset(); // Réinitialiser le formulaire en cas d'annulation
    onClose();
  };

  return (
    <Modal title={t('interview.newModal.title')} open={isOpen} onClose={handleClose}>
      <form onSubmit={handleSubmit(processSubmit)} className="space-y-4">
        <Input
          label={t('interview.newModal.titleLabel')}
          id="title" // Garder l'id pour le label htmlFor
          name="title"
          register={register}
          // Rendre le titre optionnel si c'est pour un cadrage
          rules={{ required: templateType === 'Audit' ? t('interview.newModal.titleRequired') : false }}
          error={errors.title}
          placeholder={templateType === 'Cadrage' ? t('interview.newModal.titleOptionalPlaceholder') : t('interview.newModal.titlePlaceholder')}
        />

        {/* Les champs departmentId et planType sont supprimés car non utilisés dans le payload API */}
        {/* Les champs departmentId et planType sont supprimés car non utilisés dans le payload API */}
        {/* templateType === 'Audit' && ( ... ) */}

        {/* Passer register comme prop, sans rules */}
        <Select
          label={t('interview.newModal.templateLabel')}
          name="templateId"
          register={register}
          rules={{ required: t('interview.newModal.templateRequired') }} // Ajouter la règle required
          options={[
            { value: '', label: isLoadingTemplates ? t('global.loading') : (errorTemplates ? t('interview.newModal.templatesLoadError') : t('interview.newModal.templatePlaceholder')), disabled: true },
            ...availableTemplates.map((tmpl) => ({ value: tmpl.id, label: tmpl.name })),
          ]}
          error={errors.templateId || (errorTemplates ? { type: 'custom', message: errorTemplates } : undefined)} // Afficher l'erreur de chargement aussi
          disabled={isLoadingTemplates || !!errorTemplates} // Désactiver si chargement ou erreur
        />

        {/* Afficher l'erreur de chargement des templates si elle existe */}
        {errorTemplates && !errors.templateId && (
             <p className="mt-1 text-xs text-red-600">{errorTemplates}</p>
        )}

        {/* Footer avec les boutons */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-foreground bg-background border border-border rounded-md shadow-sm hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
          >
            {t('global.cancel')}
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary border border-transparent rounded-md shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
          >
            {t('interview.newModal.submit')}
          </button>
        </div>
      </form>
    </Modal>
  );
};
